import React from 'react';
import { BOOKING_URL } from '../../../constants/config';
import {
  Container,
  Typography,
  Grid,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety'; // For benefits
import ReportProblemIcon from '@mui/icons-material/ReportProblem'; // For who should get/Symptoms
import BuildIcon from '@mui/icons-material/Build'; // For process/features
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'; // For home care
import SpaIcon from '@mui/icons-material/Spa'; // For why choose us / comfort
import StarIcon from '@mui/icons-material/Star'; // For why choose us
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const NightGuards = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Benefits specific to Night Guards
  const benefits = [
    'Protects teeth from excessive wear, chipping, and cracking.',
    'Alleviates jaw pain, soreness, and muscle fatigue.',
    'Reduces the frequency and severity of tension headaches and migraines.',
    'Helps manage symptoms associated with TMJ/TMD.',
    'Prevents damage to existing dental work like crowns and fillings.',
    'Improves sleep quality by reducing clenching and grinding.',
    'Saves money by preventing costly future dental repairs.'
  ];

  // Process specific to getting a Custom Night Guard
  const process = [
    {
      title: '1. Consultation & Examination',
      description: 'We discuss your symptoms (jaw pain, headaches, etc.) and perform a thorough examination to assess signs of bruxism and check your TMJ health.'
    },
    {
      title: '2. Precise Dental Impressions',
      description: 'We take highly accurate impressions (often using digital scanners) of your teeth to ensure your custom night guard fits perfectly and comfortably.'
    },
    {
      title: '3. Custom Fabrication',
      description: 'Your impressions are sent to a specialized dental lab where your night guard is crafted from high-quality, durable, and comfortable materials based on your specific needs (soft, hard, or dual-laminate).'
    },
    {
      title: '4. Fitting & Adjustments',
      description: 'Once ready, you’ll return for a fitting. We ensure the guard fits snugly, is comfortable, and doesn\'t interfere with your bite. Minor adjustments are made as needed.'
    },
    {
        title: '5. Care & Maintenance Instructions',
        description: 'We provide detailed instructions on how to wear, clean, and store your night guard to maximize its lifespan and effectiveness.'
    }
  ];

  // Signs you might need a Night Guard
  const whoShouldGet = [
    'Waking up with sore jaw muscles or headaches.',
    'Noticeable wear, flattening, or chipping of your teeth.',
    'Increased tooth sensitivity, especially in the morning.',
    'Hearing or being told you grind your teeth at night.',
    'Clicking, popping, or pain in your jaw joints (TMJ).',
    'Facial pain or earaches with no other apparent cause.',
    'Visible clenching marks on your tongue or cheeks.'
  ];

  // What's Included / Features
  const whatsIncluded = [
    {
      category: 'Signs You Might Need a Night Guard',
      items: [
        'Jaw Pain & Soreness',
        'Morning Headaches',
        'Worn or Damaged Teeth',
        'Tooth Sensitivity',
        'Grinding/Clenching Sounds',
        'TMJ Symptoms (Clicking/Pain)',
      ],
      icon: <ReportProblemIcon sx={{ color: 'primary.main' }}/>
    },
    {
      category: 'Features of Our Custom Night Guards',
      items: [
        'Precision Fit for Maximum Comfort',
        'High-Quality, Durable Materials',
        'Designed to Your Specific Needs (Bruxism/TMJ)',
        'Available in Soft, Hard, or Hybrid Types',
        'Protects Enamel and Dental Work',
        'Easy to Clean & Maintain',
      ],
      icon: <BuildIcon sx={{ color: 'primary.main' }}/>
    }
  ];

  // Home care tips for Night Guards
  const homeCareTips = [
    'Rinse your night guard with cool water before and after each use.',
    'Clean it daily with a soft toothbrush and mild soap or non-abrasive toothpaste.',
    'Perform a deeper clean weekly using a denture cleaner or a vinegar/water solution (follow instructions).',
    'Always store your night guard in its protective case when not in use.',
    'Keep the case clean and dry.',
    'Avoid hot water, as it can warp the material.',
    'Bring your night guard to your regular dental check-ups for inspection.'
  ];
  
  // FAQs for Night Guards
  const faqs = [
    {
      question: 'What is Bruxism?',
      answer: 'Bruxism is a condition characterized by the grinding, gnashing, or clenching of your teeth. It often occurs unconsciously, especially during sleep (sleep bruxism), but can also happen while awake.'
    },
    {
      question: 'Is a custom night guard better than an over-the-counter one?',
      answer: 'Yes, significantly. Custom-fit night guards made by a dentist offer superior comfort, fit, and protection. Over-the-counter options can be ill-fitting, less effective, and sometimes even worsen certain jaw problems.'
    },
    {
      question: 'Will a night guard feel uncomfortable?',
      answer: 'There might be a short adjustment period, but custom night guards are designed to be as comfortable as possible. Most patients find they get used to wearing it within a few nights.'
    },
    {
        question: 'How long will my custom night guard last?',
        answer: 'The lifespan varies depending on the material and the severity of your grinding, but typically, a custom night guard can last several years with proper care.'
    },
    {
        question: 'Does dental insurance cover night guards?',
        answer: 'Coverage varies greatly by insurance plan. Many plans offer partial or full coverage, especially when deemed medically necessary for bruxism or TMJ. We can help you check your benefits.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Custom Night Guards for Bruxism & TMJ | Pacifica Dental</title>
        <meta name="description" content="Protect your teeth from grinding (bruxism) and alleviate jaw pain with a custom-fit night guard from Pacifica Dental. Schedule a consultation today." />
        <meta name="keywords" content="night guard, bruxism, teeth grinding, TMJ, jaw pain, custom night guard, [Your City] dentist, dental guard, clenching" />
      </Helmet>
      
      <Box 
        sx={{ 
          py: { xs: 6, md: 8 },
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e9eef3 100%)',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant={isMobile ? "h3" : "h2"}
              component="h1"
              textAlign="center"
              gutterBottom
              sx={{ 
                mb: 2,
                fontWeight: 700,
                color: 'primary.main'
              }}
            >
              Protect Your Smile While You Sleep
            </Typography>

            <Typography
              variant={isMobile ? "h6" : "h5"}
              component="h2"
              textAlign="center"
              gutterBottom
              sx={{ mb: 4, color: 'text.secondary', fontWeight: 500 }}
            >
              Custom Night Guards for Bruxism & TMJ Relief
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{ mb: 4, fontSize: '1.15rem', lineHeight: 1.8, color: 'text.primary' }}
            >
              Do you wake up with a sore jaw, headaches, or sensitive teeth? You might be clenching or grinding your teeth at night—a common condition known as bruxism. At Pacifica Dental, we offer custom-fit night guards, a simple and effective solution to protect your teeth, alleviate discomfort, and prevent long-term dental problems caused by bruxism and TMJ disorders.
            </Typography>
            <Divider sx={{ my: 4 }} />

            {/* Benefits Section */}
            <Grid container spacing={isMobile ? 3 : 5} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    backgroundColor: theme.palette.mode === 'light' ? 'primary.light' : 'primary.dark',
                    color: 'white',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2}}>
                    Key Benefits of Wearing a Night Guard
                  </Typography>
                  <List dense>
                    {benefits.map((benefit, index) => (
                      <ListItem key={index} sx={{ py: 0.5}}>
                        <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5}}>
                          <HealthAndSafetyIcon sx={{ color: 'white', fontSize: '1.4rem' }} />
                        </ListItemIcon>
                        <ListItemText primary={benefit} primaryTypographyProps={{fontSize: '1.05rem'}} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              {/* Process Section */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom color="primary.main" sx={{ fontWeight: 600, mb: 2}}>
                    Getting Your Custom Night Guard
                  </Typography>
                  {process.map((step, index) => (
                    <Box key={index} sx={{ mb: index === process.length - 1 ? 0 : 2.5 }}>
                      <Typography variant="h6" color="primary.dark" fontWeight="bold" sx={{fontSize: '1.1rem'}}>
                        {step.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.95rem', lineHeight: 1.6}}>
                        {step.description}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 5 }} /> 

            {/* Who Should Get a Night Guard? Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Are You a Candidate for a Night Guard?
              </Typography>
              <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? '#fff3e0' : '#4e342e' }}>
                <Typography variant="body1" paragraph sx={{ mb: 2, fontSize: '1.1rem', color: 'text.secondary' }}>
                  If you experience any of the following symptoms, a custom night guard could provide significant relief and protection:
                </Typography>
                <Grid container spacing={1}>
                    {whoShouldGet.map((item, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                            <List dense sx={{py:0}}>
                                <ListItem sx={{py:0.2}}>
                                    <ListItemIcon sx={{minWidth: 'auto', marginRight: 1, color: 'primary.main'}}>
                                        <ReportProblemIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={item} primaryTypographyProps={{fontSize: '1rem'}}/>
                                </ListItem>
                            </List>
                        </Grid>
                    ))}
                </Grid>
              </Paper>
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* What's Included / Features Section */}
            <Box sx={{ my: 5 }}>
                <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                    Signs, Symptoms & Our Solution
                </Typography>
                <Grid container spacing={3}>
                    {whatsIncluded.map((section, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <Paper elevation={2} sx={{ p: 2.5, height: '100%', borderRadius: 2 }}>
                                <Box sx={{display: 'flex', alignItems: 'center', mb:1.5}}>
                                    {section.icon}
                                    <Typography variant="h6" component="h4" sx={{fontWeight: 'bold', fontSize:'1.2rem', color: 'secondary.dark', ml:1}}>
                                        {section.category}
                                    </Typography>
                                </Box>
                                <List dense>
                                    {section.items.map((item, itemIndex) => (
                                        <ListItem key={itemIndex}>
                                            <ListItemIcon sx={{minWidth: 'auto', marginRight: 1, color: 'text.secondary'}}>
                                                <CheckCircleOutlineIcon fontSize="small"/>
                                            </ListItemIcon>
                                            <ListItemText primary={item} primaryTypographyProps={{fontSize: '0.95rem'}}/>
                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Home Care Tips Section */}
            <Box sx={{ my: 5 }}>
                <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                    Caring for Your Custom Night Guard
                </Typography>
                 <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? '#e8f5e9' : '#1b5e20' }}>
                    <Typography variant="body1" paragraph sx={{ mb: 2, fontSize: '1.1rem', color: 'text.secondary' }}>
                      Proper care ensures your night guard remains effective and hygienic. Follow these simple steps:
                    </Typography>
                    <List>
                        {homeCareTips.map((tip, index) => (
                            <ListItem key={index}>
                                <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, color: 'primary.dark'}}>
                                    <CleaningServicesIcon />
                                </ListItemIcon>
                                <ListItemText primary={tip} primaryTypographyProps={{fontSize: '1rem'}}/>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            </Box>
            
            <Divider sx={{ my: 5 }} />


            {/* FAQ Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Frequently Asked Questions About Night Guards
              </Typography>
              {faqs.map((faq, index) => (
                <Accordion key={index} elevation={2} sx={{ mb: 1.5, borderRadius: 1, '&:before': { display: 'none' } }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon color="primary"/>}
                    aria-controls={`faq-panel${index}-content`}
                    id={`faq-panel${index}-header`}
                    sx={{ '& .MuiAccordionSummary-content': { fontWeight: 'medium', fontSize: '1.1rem' } }}
                  >
                    {faq.question}
                  </AccordionSummary>
                  <AccordionDetails sx={{backgroundColor: theme.palette.mode === 'light' ? '#fafafa' : '#333' }}>
                    <Typography color="text.secondary" sx={{lineHeight: 1.7, fontSize: '1rem'}}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Why Choose Us Section */}
            <Box sx={{ mt: 6, p:3, background: theme.palette.mode === 'light' ? 'rgba(0, 123, 255, 0.05)' : 'rgba(100,100,100,0.1)', borderRadius:2 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb:3 }}>
                Why Choose Us for Your Custom Night Guard?
              </Typography>
              <Grid container spacing={3}>
                  {[
                      { title: "Precision Custom Fit", text: "We use advanced techniques (including digital scans) for incredibly accurate impressions, ensuring your night guard is comfortable and effective.", icon: <SpaIcon color="primary" /> },
                      { title: "Expert Diagnosis", text: "Our experienced dentists accurately diagnose bruxism and TMJ issues to ensure a night guard is the right solution for you.", icon: <HealthAndSafetyIcon color="primary" /> },
                      { title: "High-Quality Materials", text: "We partner with top dental labs to provide durable, safe, and comfortable night guards tailored to your needs.", icon: <BuildIcon color="primary" /> },
                      { title: "Patient-Centered Care", text: "We prioritize your comfort and understanding, guiding you through every step of the process.", icon: <StarIcon color="primary" /> }
                  ].map(item => (
                      <Grid item xs={12} sm={6} key={item.title}>
                          <Box sx={{display: 'flex', alignItems: 'flex-start'}}>
                              <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, marginTop: 0.5}}>
                                  {item.icon}
                              </ListItemIcon>
                              <Box>
                                  <Typography variant="h6" component="h4" sx={{fontWeight: 'bold', fontSize: '1.1rem'}}>{item.title}</Typography>
                                  <Typography variant="body2" color="text.secondary">{item.text}</Typography>
                              </Box>
                          </Box>
                      </Grid>
                  ))}
              </Grid>
            </Box>


            {/* Call to Action */}
            <Box sx={{ mt: 6, py: 4, textAlign: 'center', background: theme.palette.primary.main, color: 'white', borderRadius: 2 }}>
                <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{fontWeight: 600}}>
                    Stop Grinding, Start Sleeping Better!
                </Typography>
                <Typography variant="body1" sx={{mb:3, opacity: 0.9}}>
                    Don't let teeth grinding damage your smile or disrupt your life. Schedule a consultation today to see if a custom night guard is right for you.
                </Typography>
              <Button
                onClick={() => window.location.href = BOOKING_URL}
                variant="contained"
                color="secondary"
                size="large"
                sx={{
                  px: 5, 
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: 'secondary.dark',
                  }
                }}
              >
                Book Your Consultation Now
              </Button>
              <Typography variant="body2" sx={{mt: 2, opacity: 0.8}}>
                Or call us at <Link to="tel:(408) 869-0998" style={{color: 'white', fontWeight: 'bold'}}>(408) 869-0998</Link> for more information.
              </Typography>
            </Box>

          </motion.div>
        </Container>
      </Box>
    </>
  );
};

export default NightGuards;